import React, {CSSProperties, useId} from 'react';
import {DatasetPieProps, PlotDataEx} from "./interfaces";
import {DatasetDataInfoResponse} from "../../../api/apiModels";
import Plotly from "plotly.js";
import {clipboardIcon} from "./icons";
import {copyChartToClipBoard} from "./chartUtils";
import {APIResponse} from "../../../services/HttpService";
import ChartWrapper from "./ChartWrapper/ChartWrapper";
import Plot from "react-plotly.js";
import {extractNumber} from "../product-template-table/utils/utils";

const layoutConfig = {
    legend: {
        orientation: "h",
    },
    margin: {
        l: 0,
        r: 0,
        b: 0,
        t: 20,
    }
} as unknown as Plotly.Layout

const DatasetPieChart = (props: DatasetPieProps) => {
    const uniqueId = useId();
    const annotations: Plotly.Annotations[] = []

    const chartConfig: Partial<Plotly.Config> = {
        displaylogo: false,
        modeBarButtonsToRemove: ["lasso2d", "select2d"],
        modeBarButtonsToAdd: [
            {
                title: 'Copy chart to Clipboard',
                name: 'Copy chart to Clipboard',
                icon: clipboardIcon,
                click: copyChartToClipBoard
            }
        ]
    }

    const separateArray = (array: Array<DatasetDataInfoResponse>): Array<Array<DatasetDataInfoResponse>> => {
        return props.charts.map((chart) => {
            let result: Array<DatasetDataInfoResponse> = [];

            for (let i = 0; i < array.length; i++) {
                if (chart.identifiers.includes(array[i].identifier)) {
                    result.push(array[i])
                }
            }

            return result
        })
    }

    const promiseHandler = (res: APIResponse<any>) => {
        if (checkIfDataExist(res.data)) {
            return separateArray(res.data).map((arr) => {
                const values = extractValues(arr, "sum")
                const unit = extractUnit(arr)

                annotations.push({
                    showarrow: false,
                    text: `${sumArray(values).toFixed(props.centerTextConfig?.decimalPlaces || 2)} ${unit}`,
                    font: {
                        size: props.centerTextConfig?.fontSize || 18
                    }
                } as unknown as Plotly.Annotations)

                const createValueWithUnitArray = (): Array<string> => {
                    return values.map(ob => {
                        return ob + " " + unit
                    })
                }

                return {
                    type: "pie",
                    hole: 0.4,
                    values: values,
                    labels: extractLabels(arr),
                    hoverinfo: 'none',
                    hovertemplate: `<b>%{label}</b><br>Value: %{value} ${unit}<br>Percent: %{percent}<extra></extra>`,
                    text: createValueWithUnitArray(),
                    textinfo: 'text+percent',
                    textfont: {
                        size: props.reportOptions?.fontSize
                    }
                } as unknown as PlotDataEx
            })
        }

        return []
    }

    const checkIfDataExist = (array: Array<DatasetDataInfoResponse>): boolean => {
        for (let i = 0; i < array.length; i++) {
            if (array[i].data.length > 0) {
                return true
            }
        }

        return false
    }

    const extractLabels = (array: Array<DatasetDataInfoResponse>): Array<string> => {
        const result: Array<string> = [];
        for (let i = 0; i < array.length; i++) {
            result.push(array[i].title as string)
        }

        return result
    }

    const extractValues = (array: Array<DatasetDataInfoResponse>, operation: "sum" | "average") => {
        const result = [];

        for (let i = 0; i < array.length; i++) {
            let sum = 0;
            let count = 0;

            const obj = array[i];

            for (let j = 0; j < obj.data.length; j++) {
                const pair = obj.data[j];

                sum += pair.value;
                if (operation === 'average') {
                    count++;
                }
            }

            const fixed = obj.decimalPlaces || 2
            if (operation === 'average') {
                const average = count > 0 ? sum / count : 0;
                result.push(+average.toFixed(fixed));
            } else {
                result.push(+sum.toFixed(fixed));
            }
        }

        return result;
    }

    const extractUnit = (array: Array<DatasetDataInfoResponse>) => {
        const result: Array<string> = [];

        for (let i = 0; i < array.length; i++) {
            result.push(array[i].unit as string)
        }

        if (result.length > 0) {
            const referenceString = result[0];
            if (result.every((element) => element === referenceString)) {
                return referenceString
            }
        }

        console.error("Units in Pie Chart does not match")
        return ""
    }

    const sumArray = (arr: Array<number>) => {
        return arr.reduce((acc: number, num: number) => {
            return acc + num;
        }, 0);
    }

    const calculateGrid = (): CSSProperties => {
        const charts = props.charts.length;
        const chartsPerRow = props.chartsPerRow;

        const getRows = (allCharts: number, divideInto: number): number => {
            return Math.ceil(allCharts / divideInto);
        }

        if (!chartsPerRow) {
            if (charts <= 3) {
                return {gridTemplateColumns: `repeat(${charts}, 1fr)`, gridTemplateRows: `repeat(1, 1fr)`}
            }

            return {gridTemplateColumns: `repeat(3, 1fr)`, gridTemplateRows: `repeat(${getRows(charts, 3)}, 1fr)`}
        }

        return {
            gridTemplateColumns: `repeat(${chartsPerRow}, 1fr)`,
            gridTemplateRows: `repeat(${getRows(charts, chartsPerRow)}, 1fr)`
        }
    }

    if (!props.charts || (props.charts && props.charts.length === 0)) {
        return <h2 style={{display: "flex", justifyContent: "center"}}>No chart identifiers</h2>
    }

    return <ChartWrapper uniqueKey={uniqueId}
                         identifiers={
                             props.charts
                                 .flatMap(e => e.identifiers)
                                 .filter((value, index, self) => {
                                     return self.indexOf(value) === index;
                                 })}
                         showDateSelection={true}
                         promiseHandler={promiseHandler}
                         updateFrequency={props.updateFrequency}
                         loadConf={props.loadConf}
                         hideRefresh={props.hideRefresh}
                         dynamicHeight={props.dynamicHeight}
                         referenceTime={props.referenceTime}
                         datasetChart={"pie"}
                         width={props.width}
                         reportMode={props.reportMode}
    >
        {(wrapper: any) => (
            wrapper.data.length > 0
                ?
                (
                    <div style={{
                        display: "grid",
                        gridGap: "10px", ...calculateGrid()
                    }}>
                        {
                            wrapper.data.map((data: Plotly.Data, index: number) => {
                                return <Plot key={index}
                                             data={[data]}
                                             className="ea-plotly-pie-chart"
                                             config={{...chartConfig}}
                                             layout={{
                                                 ...layoutConfig,
                                                 annotations: [annotations[index]],
                                                 height: extractNumber(props.height)
                                             }}
                                />
                            })
                        }
                    </div>
                )
                :
                <h2 style={{display: "flex", justifyContent: "center"}}>No data</h2>
        )}
    </ChartWrapper>
}

export default DatasetPieChart;